"use client";
import { useEffect, useState } from "react";

import {
  useFlags as _useFlags,
  LDClient,
  useLDClient,
} from "@stadium/services/launch-darkly/library-imports";
import { LaunchDarklyError } from "@stadium/services/error-reporting/utils";

export type FlagValues<T = boolean> = { [flagKey: string]: T };

type LDClientWithFallback<T = FlagValues> = LDClient & {
  __fallbackInitialized: boolean;
  __fallbackFlags: FlagValues<T>;
};

// Cache for fallback flags promise to ensure only one fetch happens
let fallbackFlagsPromise: Promise<FlagValues> | null = null;
let fallbackFlagsCache: FlagValues | null = null;

const fetchFallbackFlags = async <T>(): Promise<FlagValues<T>> => {
  // If we already have cached data, return it immediately
  if (fallbackFlagsCache) {
    return fallbackFlagsCache as FlagValues<T>;
  }

  // If a fetch is already in progress, wait for it
  if (fallbackFlagsPromise) {
    return fallbackFlagsPromise as Promise<FlagValues<T>>;
  }

  // Start a new fetch
  const fallbackFlagsUrl = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_FALLBACK_FLAGS_URL;
  if (!fallbackFlagsUrl) {
    throw new Error("Fallback flags URL not configured");
  }

  fallbackFlagsPromise = fetch(fallbackFlagsUrl, { credentials: "include" }).then((res) =>
    res.json(),
  );

  try {
    const result = await fallbackFlagsPromise;
    fallbackFlagsCache = result;
    return result as FlagValues<T>;
  } finally {
    // Clear the promise so future calls can fetch fresh data if needed
    fallbackFlagsPromise = null;
  }
};

export const useFlags = <T>() => {
  const ldClient = useLDClient() as LDClientWithFallback<T>;
  const flags = _useFlags<FlagValues<T>>();

  const [fallbackFlags, setFallbackFlags] = useState<FlagValues<T> | null>(
    ldClient?.__fallbackFlags,
  );

  useEffect(() => {
    const initializeFallbackFlags = async () => {
      if (!ldClient || ldClient.__fallbackInitialized || fallbackFlags) {
        return;
      }
      try {
        await ldClient.waitForInitialization();
      } catch (error) {
        ldClient.setStreaming(false);
        // eslint-disable-next-line no-console
        console.warn("LaunchDarkly client failed to initialize, using fallback flags");

        try {
          const fallbackFlags: FlagValues<T> = await fetchFallbackFlags<T>();
          ldClient.__fallbackFlags = fallbackFlags;
          setFallbackFlags(fallbackFlags);
          ldClient.__fallbackInitialized = true;
        } catch (fetchError) {
          // eslint-disable-next-line no-console
          console.error("Failed to fetch fallback flags:", fetchError);
        }

        new LaunchDarklyError(error); // to capture the error in Sentry
      }
    };
    initializeFallbackFlags();
  }, [ldClient, fallbackFlags]);

  return fallbackFlags || flags;
};
